import { createSelector } from '@reduxjs/toolkit';

import { IntegrationType } from '@/shared/types/integration';
import { RootState } from '@/shared/types/state';
import {
  CHANNELS_SUPPORTED,
  CHANNELS_URL,
  CHANNELS_WS_URL,
  CONNECTIONS_CATEGORIES,
  WEB_CLIENT_URL,
} from '@/util/constants';

/** True when running on a non-standard deployment (dev, on-premise, GCP). */
const isCustomDeployment = !CHANNELS_URL.includes('channels.moveo.ai');

export const selectAccordion = createSelector(
  (state: RootState) => state.accordion,
  (accordion) => {
    return { expanded: accordion.expanded, dirty: accordion.dirty };
  }
);

/**
 * Selects all the channels that are available for the current user taking
 * into account the feature flags.
 */
export const selectAvailableChannels = createSelector(
  (state: RootState) => state.featureFlags,
  (featureFlags) => {
    const channels = CHANNELS_SUPPORTED.filter((x) => {
      // Filter out Web Voice integrations if feature flag is not enabled
      if (x === IntegrationType.WEB_VOICE && !featureFlags.web_voice) {
        return false;
      }

      // Filter out MSTAT Viber integrations if feature flag is not enabled
      if (x === IntegrationType.MSTAT_VIBER && !featureFlags.mstat_viber) {
        return false;
      }

      if (x === IntegrationType.LIVECHAT && !featureFlags.livechat) {
        return false;
      }

      return true;
    });
    return channels;
  }
);

/**
 * Selects the preview widget URL for a given integration ID.
 * Includes the host parameter for non-channels.moveo.ai deployments.
 */
export const selectPreviewWebchatUrl = createSelector(
  (_: RootState, integrationId: string) => integrationId,
  (integrationId: string): string | null => {
    if (!integrationId) {
      return null;
    }

    const params = new URLSearchParams({ integrationId });

    if (isCustomDeployment) {
      params.append('host', CHANNELS_WS_URL);
    }

    return `${WEB_CLIENT_URL}/preview?${params.toString()}`;
  }
);

/**
 * Returns the web client SDK URL.
 */
export const selectWebchatSDK = (_?: RootState) => `${WEB_CLIENT_URL}/web-client.min.js`;

/**
 * Returns the host parameter snippet for webchat init calls.
 * Empty string for standard prod deployments (channels.moveo.ai),
 * `, host: "..."` for custom/non-prod deployments.
 */
export const selectWebchatHostSnippet = (_?: RootState): string =>
  isCustomDeployment ? `, host: "${CHANNELS_WS_URL}"` : '';

/**
 * Returns the connect-and-secure script snippet for a given integration ID.
 */
export const selectWebchatConnectSnippet = createSelector(
  (_: RootState, integrationId: string | undefined) => integrationId,
  (integrationId: string | undefined): string => {
    const sdkUrl = selectWebchatSDK();
    const hostSnippet = selectWebchatHostSnippet();
    return `<script src="${sdkUrl}"></script>
<script>MoveoAI.init({ integrationId: "${integrationId ?? ''}"${hostSnippet}}).then((desk) => console.log("Moveo Connected")).catch((error) => console.error(error));
</script>`;
  }
);

/**
 * Returns the embed script snippet for a given integration ID.
 */
export const selectWebchatEmbedSnippet = createSelector(
  (_: RootState, integrationId: string | undefined) => integrationId,
  (integrationId: string | undefined): string => {
    const sdkUrl = selectWebchatSDK();
    const hostSnippet = selectWebchatHostSnippet();
    return `<script src="${sdkUrl}"></script>
<script>
  MoveoAI.init({ integrationId: "${integrationId ?? ''}"${hostSnippet},element:document.getElementById('embed')}).then((desk) => console.log("Moveo connected")).catch((error) => console.error(error));
</script>`;
  }
);

/**
 * Selects integrations categorized by type (channels or communication platforms)
 * and filtered by feature flags. Returns an object with each category containing
 * only the integrations that are available for the current user.
 */
export const selectCategorizedIntegrations = createSelector(
  [selectAvailableChannels],
  (availableChannels) => ({
    channels: CONNECTIONS_CATEGORIES.channels.filter((type) => availableChannels.includes(type)),
    communicationPlatforms: CONNECTIONS_CATEGORIES.communicationPlatforms.filter((type) =>
      availableChannels.includes(type)
    ),
  })
);
